const DEFAULT_LOCALE = 'es';

/** Canonical login URL (no locale prefix). */
export const LOGIN_PATH = '/login';

export const AUTH_PORTAL_KEY = 'mvp_auth_portal';

/** LP Workbench: crew CSV upload / new production. */
export const ORG_UPLOAD_PATH = '/org/productions/new';

/** LP Workbench home (Slate). */
export const ORG_SLATE_PATH = '/org/slate';

/** Participant / crew home after applicant login. */
export const PARTICIPANT_HOME_PATH = '/participant';

/** Advisor case workbench. */
export const ADVISOR_HOME_PATH = `/${DEFAULT_LOCALE}/advisor`;

export function getLoginPath() {
  return LOGIN_PATH;
}

export function getOrgUploadPath() {
  return ORG_UPLOAD_PATH;
}

export function getOrgSlatePath() {
  return ORG_SLATE_PATH;
}

export function getParticipantHomePath() {
  return PARTICIPANT_HOME_PATH;
}

export function localizedPath(path) {
  return `/${DEFAULT_LOCALE}${path}`;
}

/**
 * Applicant destination after login / session restore.
 * Always the participant home - not the legacy Spain immigration intake/organizer.
 */
export function resolveApplicantHubPath(_user, _opts = {}) {
  return PARTICIPANT_HOME_PATH;
}

export function getUserRole(user) {
  if (!user) return '';
  const raw = user?.role?.value ?? user?.role ?? '';
  return String(raw).toLowerCase();
}

/** Role from user payload, stored session, or JWT (routing only). */
export function getAuthRole(user = null) {
  const fromUser = getUserRole(user);
  if (fromUser) return fromUser;
  try {
    const stored = JSON.parse(localStorage.getItem('userData') || 'null');
    const fromStored = getUserRole(stored);
    if (fromStored) return fromStored;
  } catch {
    // ignore
  }
  return String(peekJwtPayload()?.role || '').toLowerCase();
}

/** Read JWT claims without verifying (routing hints only). */
function peekJwtPayload() {
  try {
    const token = localStorage.getItem('authToken');
    if (!token || !token.includes('.')) return null;
    const part = token.split('.')[1];
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/** Organization id from JWT and/or `/auth/me` user payload. */
export function getOrganizationId(user) {
  const payload = peekJwtPayload();
  const fromJwt = payload?.organization_id;
  if (fromJwt != null && fromJwt !== '') return fromJwt;
  const fromUser = user?.organization_id;
  if (fromUser != null && fromUser !== '') return fromUser;
  return null;
}

/**
 * LP / org-admin workbench (Slate + upload), not the advisor case portal.
 * True when password/org portal was used, or the JWT/user carries an organization_id.
 */
export function isOrgWorkbenchAdmin(user) {
  if (getUserRole(user) !== 'admin') return false;
  if (getOrganizationId(user) != null) return true;
  try {
    if (localStorage.getItem(AUTH_PORTAL_KEY) === 'org') return true;
  } catch {
    // ignore
  }
  return false;
}

/**
 * Persist which product surface the session belongs to (org workbench vs applicant).
 * Call after the user record / JWT is known so OAuth/phone org admins get `org`.
 */
export function rememberAuthPortal(method, user = null) {
  try {
    if (getUserRole(user) === 'advisor') {
      localStorage.setItem(AUTH_PORTAL_KEY, 'advisor');
      return;
    }
    if (method === 'password' || isOrgWorkbenchAdmin(user)) {
      localStorage.setItem(AUTH_PORTAL_KEY, 'org');
      return;
    }
    if (method === 'phone' || method === 'oauth' || method === 'email') {
      localStorage.setItem(AUTH_PORTAL_KEY, 'applicant');
    }
  } catch {
    // ignore storage errors
  }
}

/**
 * For org admins: always land on Slate (empty slate shows Add your first project).
 * Falls back to Slate if the projects API is unavailable.
 */
export async function resolveOrgWorkbenchPath() {
  return ORG_SLATE_PATH;
}

/**
 * Resolve the post-authentication destination from role and auth method.
 *
 * Org / LP admins are routed asynchronously via {@link resolveOrgWorkbenchPath}.
 * Applicants always go to {@link PARTICIPANT_HOME_PATH}.
 */
export function resolvePostLoginPath({
  user,
  method = 'phone',
} = {}) {
  const role = getUserRole(user);

  if (role === 'super_admin') {
    return '/super-admin';
  }

  if (role === 'advisor') {
    return ADVISOR_HOME_PATH;
  }

  if (role === 'admin') {
    return ORG_SLATE_PATH;
  }

  return PARTICIPANT_HOME_PATH;
}

/**
 * Full post-login navigation target (async when org admin needs project check).
 */
export async function resolvePostLoginPathAsync({
  user,
  method = 'phone',
} = {}) {
  const role = getUserRole(user);

  if (role === 'admin') {
    return resolveOrgWorkbenchPath();
  }

  return resolvePostLoginPath({
    user,
    method,
  });
}

export async function resolveSessionRestorePathAsync(user) {
  const role = getUserRole(user);

  if (role === 'super_admin') {
    return '/super-admin';
  }

  if (role === 'advisor') {
    return ADVISOR_HOME_PATH;
  }

  if (role === 'admin') {
    return resolveOrgWorkbenchPath();
  }

  return PARTICIPANT_HOME_PATH;
}

/** @deprecated Prefer resolveSessionRestorePathAsync for org admins. */
export function resolveSessionRestorePath(user) {
  const role = getUserRole(user);

  if (role === 'super_admin') {
    return '/super-admin';
  }

  if (role === 'advisor') {
    return ADVISOR_HOME_PATH;
  }

  if (role === 'admin') {
    return ORG_SLATE_PATH;
  }

  return PARTICIPANT_HOME_PATH;
}
